import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Phone, Plus, ShoppingBag, Truck } from "lucide-react";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useCart } from "../../contexts/CartContext";
import {
  METALS,
  attribute,
  categoryGroup,
  jewelleryType,
  kicker,
  opalType,
  pricing,
  isOutOfStock,
} from "../store/storeTaxonomy";
import { STORE_PHONE, STORE_TEL, formatAUD } from "../store/ShopParts";
import "./product-detail.css";

/**
 * /opal-store/product/:productId — light-gallery product page.
 * Sticky gallery, price + stock, description, optional metal choice, buy row,
 * certificate callout, specifications and a sticky add-to-bag bar (desktop:
 * once the buy row scrolls under the header; mobile: always).
 */

const CATALOGUE = "/online-store/shop";
const HEADER_H = 104;

function useMedia(query) {
  const [on, setOn] = useState(() => typeof window !== "undefined" && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setOn(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", onChange) : mq.addListener(onChange);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", onChange) : mq.removeListener(onChange));
  }, [query]);
  return on;
}

const RProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [metal, setMetal] = useState(null);
  const [buyRowHidden, setBuyRowHidden] = useState(false);
  const buyRef = useRef(null);
  const mobile = useMedia("(max-width: 759px)");
  const { addToCart } = useCart();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setSelectedImageIndex(0);
    setQuantity(1);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/${productId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Product not found");
        return res.json();
      })
      .then((data) => {
        if (!alive) return;
        setProduct(data);
        // Default to the first metal, so there's no "please choose" step
        const options = data.has_metal_options ? (data.metal_options || []).filter((m) => METALS[m]) : [];
        setMetal(options[0] || null);
      })
      .catch((err) => alive && setError(err.message))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [productId]);

  // Desktop: show the sticky bar once the buy row has scrolled up under the header
  useEffect(() => {
    const el = buyRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setBuyRowHidden(!entry.isIntersecting && entry.boundingClientRect.top < HEADER_H),
      { rootMargin: `-${HEADER_H}px 0px 0px 0px` }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [product, loading]);

  /* -------- Loading -------- */
  if (loading) {
    return (
      <MarketingShell pageClass="pg-shop pg-product-detail">
        <SEOHead
          title="Loading Product"
          description="Loading product details from the Umoona Opal Mine online store."
          noIndex
        />
        <section className="sh-status">
          <div className="sh-spinner" />
          <p className="sh-status__text">Loading product...</p>
        </section>
      </MarketingShell>
    );
  }

  /* -------- Not found -------- */
  if (error || !product) {
    return (
      <MarketingShell pageClass="pg-shop pg-product-detail">
        <SEOHead
          title="Product Not Found"
          description="The product you are looking for does not exist or is no longer available."
          noIndex
        />
        <section className="sh-status">
          <div className="sh-status__icon">
            <ShoppingBag size={34} />
          </div>
          <h1 className="sh-status__title">Product Not Found</h1>
          <p className="sh-status__copy">
            This piece may have sold or been taken off the online store. Have a look at the rest of the collection.
          </p>
          <div className="sh-status__btns">
            <Link to={CATALOGUE} className="sh-btn sh-btn--accent">
              Shop the collection
            </Link>
          </div>
        </section>
      </MarketingShell>
    );
  }

  const { price, regular, onSale } = pricing(product);
  const hasPrice = price > 0;
  const outOfStock = isOutOfStock(product);
  const images = [...(product.images || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
  const mainSrc = images.length ? images[Math.min(selectedImageIndex, images.length - 1)].src : "";
  const opal = opalType(product);
  const jewel = jewelleryType(product);
  const souvenir = categoryGroup(product) === "Souvenirs & gifts";
  const metalOptions = product.has_metal_options ? (product.metal_options || []).filter((m) => METALS[m]) : [];
  const metalInfo = metal && METALS[metal];

  const typeCrumb = opal
    ? { label: `${opal} opal`, to: `${CATALOGUE}?opal=${opal}` }
    : jewel
    ? { label: `Opal ${jewel.toLowerCase()}`, to: `${CATALOGUE}?type=${jewel}` }
    : { label: "Souvenirs", to: `${CATALOGUE}?category=Souvenirs+%26+gifts` };

  const setting = metalInfo ? metalInfo.long : attribute(product, "Metal");
  const specs = souvenir
    ? [
        ["Made in", attribute(product, "Made in") || "Australia"],
        ["SKU", product.sku],
      ]
    : [
        ["Opal type", opal ? `${opal} opal` : attribute(product, "Opal type")],
        ["Carat weight", attribute(product, "Carat weight")],
        ["Stone size", attribute(product, "Stone size")],
        ["Origin", "Coober Pedy, South Australia"],
        ["Setting", setting],
        ["Sold as", attribute(product, "Sold as")],
        ["SKU", product.sku],
      ];

  const add = () => addToCart(product, quantity, metal);
  const canBuy = hasPrice && !outOfStock;
  const showSticky = canBuy && (mobile || buyRowHidden);

  const plainDescription = product.description
    ? product.description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
    : `Authentic Australian opal from Umoona Opal Mine in Coober Pedy — ${product.name}.`;

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: plainDescription,
    sku: product.sku || undefined,
    image: images.length > 0 ? images.map((img) => img.src) : undefined,
    brand: { "@type": "Brand", name: "Umoona Opal Mine" },
    offers: {
      "@type": "Offer",
      priceCurrency: "AUD",
      price: hasPrice ? price.toFixed(2) : undefined,
      availability: outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      url: `https://umoonaopalmine.com.au/opal-store/product/${productId}`,
    },
  };

  return (
    <MarketingShell pageClass={"pg-shop pg-product-detail" + (showSticky && mobile ? " has-stickybar" : "")}>
      <SEOHead
        title={product.name}
        description={plainDescription}
        canonical={`/opal-store/product/${productId}`}
        ogImage={images.length > 0 ? images[0].src : undefined}
        ogType="product"
        schemaData={schemaData}
      />

      <section className="pd-sect">
        <div className="sh-wrap">
          <nav className="sh-crumbs pd-crumbs" aria-label="Breadcrumb">
            <Link to="/online-store">Opal Store</Link>
            <span>/</span>
            <Link to={CATALOGUE}>Shop</Link>
            <span>/</span>
            <Link to={typeCrumb.to}>{typeCrumb.label}</Link>
            <span>/</span>
            <span className="sh-crumbs__here">{product.name}</span>
          </nav>

          <div className="pd-layout">
            {/* ---- Gallery ---- */}
            <div className="pd-gallery">
              {images.length > 1 && (
                <div className="pd-thumbs">
                  {images.map((img, i) => (
                    <button
                      key={img.id || img.src}
                      type="button"
                      className={"pd-thumb" + (i === selectedImageIndex ? " is-active" : "")}
                      onClick={() => setSelectedImageIndex(i)}
                      aria-label={`View image ${i + 1}`}
                    >
                      <img src={img.src} alt="" loading="lazy" decoding="async" />
                    </button>
                  ))}
                </div>
              )}
              <div className="pd-main">{mainSrc ? <img src={mainSrc} alt={product.name} /> : null}</div>
            </div>

            {/* ---- Info ---- */}
            <div className="pd-info">
              <div>
                <div className="pd-kicker">{kicker(product)}</div>
                <h1 className="pd-title">{product.name}</h1>
                <div className="pd-pricerow">
                  {hasPrice ? (
                    <span className="pd-price">
                      {formatAUD(price)}
                      {onSale && <span className="pd-price__was">{formatAUD(regular)}</span>}
                    </span>
                  ) : (
                    <span className="pd-price">Call for Price</span>
                  )}
                  {outOfStock ? (
                    <span className="pd-stock is-out">
                      <span className="pd-stock__dot" />
                      Out of stock
                    </span>
                  ) : (
                    <span className="pd-stock">
                      <span className="pd-stock__dot" />
                      In stock · ships from Coober Pedy
                    </span>
                  )}
                </div>
              </div>

              {product.description && (
                <div className="pd-desc" dangerouslySetInnerHTML={{ __html: product.description }} />
              )}

              {metalOptions.length > 0 && (
                <div>
                  <div className="pd-metal__label">
                    Metal <span>— {metalInfo ? metalInfo.long : "choose one"}</span>
                  </div>
                  <div className="pd-metal__opts">
                    {metalOptions.map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={"pd-metal" + (metal === m ? " is-on" : "")}
                        aria-pressed={metal === m}
                        onClick={() => setMetal(m)}
                      >
                        <span className="pd-metal__swatch" style={{ background: METALS[m].swatch }} />
                        {METALS[m].label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div ref={buyRef}>
                {canBuy ? (
                  <div className="pd-buy">
                    <div className="pd-qty">
                      <button type="button" aria-label="Decrease quantity" onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
                        <Minus size={15} />
                      </button>
                      <span>{quantity}</span>
                      <button type="button" aria-label="Increase quantity" onClick={() => setQuantity((q) => q + 1)}>
                        <Plus size={15} />
                      </button>
                    </div>
                    <button type="button" className="pd-addbtn" onClick={add}>
                      <ShoppingBag size={18} />
                      Add to bag — {formatAUD(price * quantity)}
                    </button>
                  </div>
                ) : hasPrice ? (
                  <button type="button" className="pd-addbtn" disabled>
                    Out of stock
                  </button>
                ) : (
                  <a href={STORE_TEL} className="pd-addbtn pd-callbtn">
                    <Phone size={18} />
                    Call for Price — {STORE_PHONE}
                  </a>
                )}
              </div>
              <div className="pd-help">
                Questions about this piece? <a href={STORE_TEL}>Call {STORE_PHONE}</a>
              </div>

              {!souvenir && (
                <div className="pd-cert">
                  <img src="/design/assets/umoona-emblem.png" alt="" />
                  <div>
                    <div className="pd-cert__title">Certificate of authenticity included</div>
                    <div className="pd-cert__copy">
                      Ships with an Umoona certificate recording the opal type, weight and origin — your proof it's
                      genuine Australian opal.
                    </div>
                  </div>
                </div>
              )}

              <div>
                <div className="pd-specs__title">{souvenir ? "Details" : "Opal specifications"}</div>
                {specs
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="pd-spec">
                      <span>{k}</span>
                      <span>{v}</span>
                    </div>
                  ))}
              </div>

              <div className="pd-ship">
                <Truck size={18} />
                Australia-wide shipping, dispatched from Coober Pedy. Australian addresses only.
              </div>
            </div>
          </div>
        </div>
      </section>

      {showSticky && (
        <div className="pd-sticky">
          <div className="sh-wrap pd-sticky__inner">
            {mainSrc ? <img src={mainSrc} alt="" /> : null}
            <div className="pd-sticky__text">
              <div className="pd-sticky__name">{product.name}</div>
              <div className="pd-sticky__meta">
                {formatAUD(price)}
                {metalInfo ? ` · ${metalInfo.label}` : ""}
              </div>
            </div>
            <button type="button" className="pd-sticky__btn" onClick={add}>
              Add to bag
            </button>
          </div>
        </div>
      )}
    </MarketingShell>
  );
};

export default RProductDetailPage;
